import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import { queryCacheScope } from "./query-cache-scope";
import { getQueryClient, peekQueryClient } from "@/queries/client";
import { projectWorklogSummaryKey, projectWorklogSummaryQuery } from "@/queries/project-resources";
import type { TIssueWorklog, TIssueWorklogCreate, TIssueWorklogUpdate } from "@/types/worklog";

/**
 * The keys an edit changed, for `worklog_updated` (NG-2056). The dialog sends
 * every field, so compare against the entry as it was. The entry names its
 * person `logged_by` where the payload says `logged_by_id`, and the payload's
 * `logged_at` is a bare date-time the entry returns in full, so compare days.
 * A field the dialog leaves `undefined` wasn't sent.
 */
function changedWorklogFields(data: TIssueWorklogUpdate, old: TIssueWorklog | undefined): string[] {
  const before: Record<string, unknown> | undefined = old && {
    duration: old.duration,
    description: old.description,
    timezone: old.timezone,
    logged_at: old.logged_at?.slice(0, 10),
    logged_by_id: old.logged_by,
  };
  return Object.entries(data)
    .filter(([, value]) => value !== undefined)
    .filter(([key, value]) => {
      if (!before) return true;
      const next = key === "logged_at" && typeof value === "string" ? value.slice(0, 10) : value;
      return next !== before[key];
    })
    .map(([key]) => key);
}

export class WorklogStore {
  entriesByIssueId = observable.map<string, TIssueWorklog[]>();
  isLoadingByIssueId = observable.map<string, boolean>();
  errorByIssueId = observable.map<string, string | null>();
  mutatingIssueId: string | null = null;
  projectSummary = observable.map<string, Record<string, number>>();
  isLoadingProjectSummary = observable.map<string, boolean>();
  /**
   * Issues whose total has been fetched, as `${slug}|${issueId}`.
   *
   * `projectSummary` can't answer "did we fetch this?" — an issue with nothing
   * logged is legitimately absent from it, so absence is indistinguishable from
   * not-yet-asked. This set is what lets a paginated list ask only about its new
   * rows instead of re-totalling the whole page on every append.
   */
  summarizedIssueIds = observable.set<string>();

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  getEntries(issueId: string): TIssueWorklog[] {
    return this.entriesByIssueId.get(issueId) ?? [];
  }

  getTotalSeconds(issueId: string): number {
    return this.getEntries(issueId).reduce((sum, e) => sum + (e.duration || 0), 0);
  }

  getProjectSummarySeconds(slug: string, projectId: string, issueId: string): number {
    return this.projectSummary.get(`${slug}|${projectId}`)?.[issueId] ?? 0;
  }

  async fetchIssueWorklogs(
    slug: string,
    projectId: string,
    issueId: string,
  ): Promise<TIssueWorklog[]> {
    this.isLoadingByIssueId.set(issueId, true);
    this.errorByIssueId.set(issueId, null);
    try {
      const list = await this.rootStore.worklogService.listIssueWorklogs(slug, projectId, issueId);
      runInAction(() => {
        this.entriesByIssueId.set(issueId, list);
      });
      return list;
    } catch (error) {
      runInAction(() => {
        this.errorByIssueId.set(issueId, (error as Error).message ?? "error");
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoadingByIssueId.set(issueId, false);
      });
    }
  }

  async createIssueWorklog(
    slug: string,
    projectId: string,
    issueId: string,
    data: TIssueWorklogCreate,
    source: AnalyticsSource = "page",
  ): Promise<TIssueWorklog> {
    this.mutatingIssueId = issueId;
    try {
      const created = await this.rootStore.worklogService.createIssueWorklog(
        slug,
        projectId,
        issueId,
        data,
      );
      runInAction(() => {
        this.entriesByIssueId.set(issueId, [
          created,
          // A fetch that read after the server saved this entry may have
          // cached it already; prepending again would list it twice (NG-2667).
          ...(this.entriesByIssueId.get(issueId) ?? []).filter((e) => e.id !== created.id),
        ]);
        const key = `${slug}|${projectId}`;
        const summary = this.projectSummary.get(key);
        if (summary) {
          summary[issueId] = (summary[issueId] ?? 0) + created.duration;
          this.projectSummary.set(key, summary);
        }
      });
      this.invalidateCachedSummary(slug, projectId);
      track("worklog_created", {
        worklog_id: created.id,
        task_id: issueId,
        project_id: projectId,
        source,
      });
      return created;
    } finally {
      runInAction(() => {
        this.mutatingIssueId = null;
      });
    }
  }

  async updateIssueWorklog(
    slug: string,
    projectId: string,
    issueId: string,
    worklogId: string,
    data: TIssueWorklogUpdate,
    source: AnalyticsSource = "page",
  ): Promise<TIssueWorklog> {
    // Read before the write replaces it: `fields` is what this edit changed.
    const fields = changedWorklogFields(
      data,
      (this.entriesByIssueId.get(issueId) ?? []).find((e) => e.id === worklogId),
    );
    this.mutatingIssueId = issueId;
    try {
      const updated = await this.rootStore.worklogService.updateIssueWorklog(
        slug,
        projectId,
        issueId,
        worklogId,
        data,
      );
      runInAction(() => {
        const prev = this.entriesByIssueId.get(issueId) ?? [];
        const old = prev.find((e) => e.id === worklogId);
        this.entriesByIssueId.set(
          issueId,
          prev.map((e) => (e.id === worklogId ? updated : e)),
        );
        const key = `${slug}|${projectId}`;
        const summary = this.projectSummary.get(key);
        if (summary && old) {
          summary[issueId] = Math.max(0, (summary[issueId] ?? 0) - old.duration + updated.duration);
          this.projectSummary.set(key, summary);
        }
      });
      this.invalidateCachedSummary(slug, projectId);
      if (fields.length > 0) {
        track("worklog_updated", {
          worklog_id: worklogId,
          task_id: issueId,
          project_id: projectId,
          fields,
          source,
        });
      }
      return updated;
    } finally {
      runInAction(() => {
        this.mutatingIssueId = null;
      });
    }
  }

  async deleteIssueWorklog(
    slug: string,
    projectId: string,
    issueId: string,
    worklogId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.mutatingIssueId = issueId;
    try {
      await this.rootStore.worklogService.deleteIssueWorklog(slug, projectId, issueId, worklogId);
      runInAction(() => {
        const prev = this.entriesByIssueId.get(issueId) ?? [];
        const deleted = prev.find((e) => e.id === worklogId);
        this.entriesByIssueId.set(
          issueId,
          prev.filter((e) => e.id !== worklogId),
        );
        const key = `${slug}|${projectId}`;
        const summary = this.projectSummary.get(key);
        if (summary && deleted) {
          summary[issueId] = Math.max(0, (summary[issueId] ?? 0) - deleted.duration);
          this.projectSummary.set(key, summary);
        }
      });
      this.invalidateCachedSummary(slug, projectId);
      track("worklog_deleted", {
        worklog_id: worklogId,
        task_id: issueId,
        project_id: projectId,
        source,
      });
    } finally {
      runInAction(() => {
        this.mutatingIssueId = null;
      });
    }
  }

  /**
   * A logged-time write changes the project's summary. `projectSummary` is
   * patched in place; the cached summary (R7) is invalidated rather than
   * patched, so the next read asks the server instead of putting back the
   * totals from before the write.
   */
  private invalidateCachedSummary(slug: string, projectId: string): void {
    const scope = queryCacheScope(this.rootStore, slug, "WorklogStore");
    if (!scope) return;
    peekQueryClient()
      ?.invalidateQueries({ queryKey: projectWorklogSummaryKey(scope, projectId), exact: true })
      .catch((error: unknown) => {
        console.warn("[WorklogStore] invalidating the cached worklog summary failed", {
          slug,
          projectId,
          error,
        });
      });
  }

  /**
   * Logged seconds per work item in one project, for the overview and the
   * project's work-items list. With the `query-cache` flag on, the read goes
   * through the cached summary (R7, `app/queries/project-resources.ts`), so
   * re-entering the project within its freshness window makes no request; the
   * rows are then projected into `projectSummary`, which every reader still
   * renders from.
   */
  async fetchProjectWorklogSummary(
    slug: string,
    projectId: string,
  ): Promise<Record<string, number>> {
    const key = `${slug}|${projectId}`;
    this.isLoadingProjectSummary.set(key, true);
    try {
      const scope = queryCacheScope(this.rootStore, slug, "WorklogStore");
      const rows = scope
        ? await getQueryClient().fetchQuery(
            projectWorklogSummaryQuery(this.rootStore.worklogService, scope, projectId),
          )
        : await this.rootStore.worklogService.getProjectWorklogSummary(slug, projectId);
      const map: Record<string, number> = {};
      for (const r of rows) map[r.issue_id] = r.duration;
      runInAction(() => {
        this.projectSummary.set(key, map);
      });
      return map;
    } finally {
      runInAction(() => {
        this.isLoadingProjectSummary.set(key, false);
      });
    }
  }

  /**
   * Load logged totals for a cross-project set of work items in one request.
   *
   * Writes into the same `projectSummary` map the per-project fetch uses, keyed
   * by each row's own project, so `getProjectSummarySeconds` and the
   * create/update/delete total adjustments all keep working unchanged.
   *
   * A record is seeded for **every** project in `items`, even one with no logged
   * time at all. That is load-bearing, not defensive: the create/update/delete
   * paths only adjust a total `if (summary)` — with no key for the project, time
   * logged on it from a cross-project list would never reach the cell.
   *
   * `items` may repeat ids and projects; both are de-duplicated here.
   */
  async fetchWorkspaceWorklogSummary(
    slug: string,
    items: { issueId: string; projectId: string }[],
  ): Promise<void> {
    const projectByIssue = new Map<string, string>();
    for (const { issueId, projectId } of items) {
      // Skip ids already totalled. Without this, an infinite-scroll list refires
      // for its whole accumulated page on every append — O(n²) requests, and the
      // only way the id set ever reaches the endpoint's cap.
      if (issueId && projectId && !this.summarizedIssueIds.has(`${slug}|${issueId}`)) {
        projectByIssue.set(issueId, projectId);
      }
    }
    if (projectByIssue.size === 0) return;

    const rows = await this.rootStore.worklogService.getWorkspaceWorklogSummary(slug, [
      ...projectByIssue.keys(),
    ]);

    // One pass over the requested items, so the empty-record seed and the fill
    // are the same statement and can't drift: every project gets a record, and
    // an issue with no logged time simply contributes no key to it.
    const durationByIssue = new Map(rows.map((r) => [r.issue_id, r.duration]));
    const totalsByProject = new Map<string, Record<string, number>>();
    for (const [issueId, projectId] of projectByIssue) {
      const totals = totalsByProject.get(projectId) ?? {};
      const seconds = durationByIssue.get(issueId);
      if (seconds !== undefined) totals[issueId] = seconds;
      totalsByProject.set(projectId, totals);
    }

    this.applyProjectTotals(slug, totalsByProject, projectByIssue.keys());
  }

  /**
   * Commit per-project totals and mark their issues summarized. The shared tail
   * of `fetchWorkspaceWorklogSummary` and `hydrateWorkspaceWorklogSummary`,
   * which differ only in how they BUILD the totals.
   *
   * Merges rather than replaces: the project's own surface may already have
   * fetched its full summary, which covers issues outside this page.
   *
   * `issueIds` is passed separately rather than read back off `totalsByProject`
   * because an issue with nothing logged may contribute no key to it, and it
   * still has to be marked — otherwise the paginated fetch path re-requests it
   * on every append.
   */
  private applyProjectTotals(
    slug: string,
    totalsByProject: Map<string, Record<string, number>>,
    issueIds: Iterable<string>,
  ): void {
    runInAction(() => {
      for (const [projectId, totals] of totalsByProject) {
        const key = `${slug}|${projectId}`;
        this.projectSummary.set(key, { ...this.projectSummary.get(key), ...totals });
      }
      for (const issueId of issueIds) {
        this.summarizedIssueIds.add(`${slug}|${issueId}`);
      }
    });
  }

  /**
   * Seed the per-issue totals from a list response that already carries them,
   * with no request.
   *
   * The counterpart to `fetchWorkspaceWorklogSummary` for a caller whose own
   * payload includes the same sum — today the My Work feed, whose rows carry
   * `logged_seconds` so the server can ORDER BY Actual hours (NG-493). Fetching
   * the summary as well would ask for a number the caller was just handed.
   *
   * Keeps the store as the single render source, so an inline edit still
   * updates the cell through the create/update/delete paths rather than
   * needing the list to refetch.
   *
   * Writes an explicit `0` where nothing is logged, rather than omitting the key
   * the way the endpoint's response does. The list response is authoritative for
   * every id in it, so a `0` has to be able to CLEAR a total this store already
   * holds — otherwise time deleted on another surface would linger in the cell
   * until reload.
   */
  hydrateWorkspaceWorklogSummary(
    slug: string,
    items: { issueId: string; projectId: string; seconds: number | null }[],
  ): void {
    // One pass, so the totals and the ids marked summarized cannot disagree
    // about which items counted. An id marked without a total would suppress
    // the fetch path for it permanently.
    const totalsByProject = new Map<string, Record<string, number>>();
    const issueIds: string[] = [];
    for (const { issueId, projectId, seconds } of items) {
      if (!issueId || !projectId) continue;
      const totals = totalsByProject.get(projectId) ?? {};
      totals[issueId] = seconds ?? 0;
      totalsByProject.set(projectId, totals);
      issueIds.push(issueId);
    }
    this.applyProjectTotals(slug, totalsByProject, issueIds);
  }

  reset(): void {
    this.entriesByIssueId.clear();
    this.isLoadingByIssueId.clear();
    this.errorByIssueId.clear();
    this.mutatingIssueId = null;
    this.projectSummary.clear();
    this.isLoadingProjectSummary.clear();
    this.summarizedIssueIds.clear();
  }
}
